import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { X, Mail, Lock, User, Eye, EyeOff, Chrome } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthModal({ onClose, defaultTab = 'login' }) {
  const { loginWithRedirect } = useAuth0();
  const { t } = useLanguage();
  const [tab, setTab] = useState(defaultTab); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email: form.email, password: form.password });
      if (res.data?.token) {
        localStorage.setItem('ct_token', res.data.token);
        localStorage.setItem('ct_user', JSON.stringify(res.data.user));
        window.dispatchEvent(new Event('ct_auth_change'));
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Netočni podaci za prijavu.');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Lozinke se ne podudaraju.'); return; }
    if (form.password.length < 8) { setError('Lozinka mora imati najmanje 8 znakova.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/register`, { name: form.name, email: form.email, password: form.password });
      // Flag for onboarding survey on next login
      try { localStorage.setItem('ct_onboarding_pending', form.email); } catch {}
      setSuccess('Račun stvoren! Možete se sada prijaviti.');
      setTab('login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registracija neuspješna. Pokušajte ponovo.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_d378754e-4674-4e88-b637-20552b388a6b/artifacts/82cxbgvt_CroatiaTimesLogoWebCMS.png" alt="CT" className="h-8 w-auto" />
            <div>
              <h2 className="font-serif font-bold text-navy dark:text-white text-lg leading-tight">Croatia Times</h2>
              <p className="text-xs text-gray-400">Hrvatska danas</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-gray-700">
          <button onClick={() => setTab('login')} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'login' ? 'text-navy dark:text-white border-b-2 border-terracotta' : 'text-gray-400 hover:text-gray-600'}`}>
            Prijava
          </button>
          <button onClick={() => setTab('register')} className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === 'register' ? 'text-navy dark:text-white border-b-2 border-terracotta' : 'text-gray-400 hover:text-gray-600'}`}>
            Registracija
          </button>
        </div>

        <div className="p-6">
          {/* Auth0 Social login */}
          <button
            onClick={() => { loginWithRedirect(); onClose(); }}
            data-testid="auth0-login-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-navy text-navy dark:border-white dark:text-white rounded-xl text-sm font-medium hover:bg-navy hover:text-white dark:hover:bg-white dark:hover:text-navy transition-colors mb-3"
          >
            <Chrome size={18} />
            Prijava s Auth0 (Google / GitHub)
          </button>

          {/* Emergent Google OAuth - direct */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            onClick={() => {
              const redirectUrl = window.location.origin + '/';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            data-testid="google-oauth-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="text-gray-700 dark:text-gray-300">Prijava s Google</span>
          </button>

          {/* Facebook Login */}
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/api/auth/facebook?origin=${encodeURIComponent(window.location.origin)}`);
                if (res.data?.url) window.location.href = res.data.url;
              } catch { alert('Facebook login unavailable'); }
            }}
            data-testid="facebook-login-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#1877F2] text-white rounded-xl text-sm font-medium hover:bg-[#166FE5] transition-colors mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            <span>Prijava s Facebook</span>
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-gray-400">{t('orContinueWith')} e-mailom</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">{success}</div>}

          {tab === 'login' ? (
            <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="E-mail adresa" className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-700 dark:text-white" />
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPass ? 'text' : 'password'} required value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Lozinka" className="w-full pl-10 pr-10 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-700 dark:text-white" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-navy text-white rounded-xl text-sm font-medium hover:bg-navy-dark disabled:opacity-50 transition-colors">
                {loading ? 'Prijava...' : 'Prijavi se'}
              </button>
              <p className="text-center text-sm text-gray-500">
                {t('noAccount')}{' '}
                <button type="button" onClick={() => setTab('register')} className="text-terracotta hover:underline font-medium">Registrirajte se</button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ime i prezime" className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-700 dark:text-white" />
              </div>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="E-mail adresa" className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-700 dark:text-white" />
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPass ? 'text' : 'password'} required value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Lozinka (min. 8 znakova)" className="w-full pl-10 pr-10 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-700 dark:text-white" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="password" required value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} placeholder="Potvrdi lozinku" className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:border-navy dark:bg-gray-700 dark:text-white" />
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-terracotta text-white rounded-xl text-sm font-medium hover:bg-terracotta-dark disabled:opacity-50 transition-colors">
                {loading ? 'Stvaranje računa...' : 'Stvori račun'}
              </button>
              <p className="text-center text-sm text-gray-500">
                {t('alreadyHaveAccount')}{' '}
                <button type="button" onClick={() => setTab('login')} className="text-terracotta hover:underline font-medium">Prijavite se</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
